/**
 * Plus Codes (Open Location Code), encoder only — item 246.
 *
 * A Plus Code names a 14 m square anywhere on Earth with ten characters, e.g. `9C2Q9RHP+X5`.
 * The code is arithmetic on the coordinates: no lookup, no quota, no storage. This is a typed
 * port of the ten-character ("pair") part of Google's reference implementation
 * (github.com/google/open-location-code, Apache License 2.0), checked against its test vectors.
 */

const ALPHABET = '23456789CFGHJMPQRVWX'
const BASE = 20
const LAT_MAX = 90
const LNG_MAX = 180
const PAIR_DIGITS = 10
// Integer multipliers for the full-precision (15-digit) code: 20^3 pair steps times the
// 5x4 grid refinements. A ten-character code drops the five grid digits (see below).
const LAT_MULT = 8000 * 3125 // 20^3 * 5^5
const LNG_MULT = 8000 * 1024 // 20^3 * 4^5
const GRID_LAT_DIVISOR = 3125 // 5^5
const GRID_LNG_DIVISOR = 1024 // 4^5

/**
 * The ten-character global Plus Code for a point, or `null` when a coordinate is not a finite
 * number. Latitude is clipped to ±90 and longitude wrapped into ±180, as the standard requires.
 */
export function encodePlusCode(lat: number, lng: number): string | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

  // Positive integers; the round-then-floor guards against float noise such as 0.0000625.
  let latVal = Math.floor(Math.round((lat + LAT_MAX) * LAT_MULT * 1e6) / 1e6)
  const latSpan = 2 * LAT_MAX * LAT_MULT
  if (latVal < 0) latVal = 0
  else if (latVal >= latSpan) latVal = latSpan - 1

  let lngVal = Math.floor(Math.round((lng + LNG_MAX) * LNG_MULT * 1e6) / 1e6)
  const lngSpan = 2 * LNG_MAX * LNG_MULT
  if (lngVal < 0) lngVal = (lngVal % lngSpan) + lngSpan
  else if (lngVal >= lngSpan) lngVal = lngVal % lngSpan

  latVal = Math.floor(latVal / GRID_LAT_DIVISOR)
  lngVal = Math.floor(lngVal / GRID_LNG_DIVISOR)

  let code = ''
  for (let i = 0; i < PAIR_DIGITS / 2; i++) {
    code = ALPHABET.charAt(lngVal % BASE) + code
    code = ALPHABET.charAt(latVal % BASE) + code
    latVal = Math.floor(latVal / BASE)
    lngVal = Math.floor(lngVal / BASE)
    if (i === 0) code = '+' + code
  }
  return code
}
